/**
 * =========================================================================================
 *     DOBLER DATA SOLUTIONS, LLC - PROPRIETARY AND CONFIDENTIAL
 * =========================================================================================
 * The source code contained herein is owned by Dobler Data Solutions, LLC. The source code
 * and all other copyrightable materials related thereto, including without limitation, all
 * text, logos, designs, graphics, other files, and other code, is protected under Title 17
 * of the United States Code. Any use, including any commercial use, distribution,
 * republication, or display of the source code or any other copyrightable materials related
 * thereto is strictly prohibited. Copyright © 2026 Dobler Data Solutions, LLC.
 * ALL RIGHTS RESERVED.
 * =========================================================================================
 *
 *     File Name       :   NavSidebar.tsx
 *     Description     :   Responsive navigation sidebar — renders the navRoutes registry (icons +
 *                         labels, gating adminOnly). Off-canvas drawer on mobile/tablet; on desktop a
 *                         static rail that collapses to icons-only. Token-styled for dark mode.
 *     Module          :   005-web-shell
 *     Component Type  :   Component
 *
 * -----------------------------------------------------------------------------------------
 *     CHANGE HISTORY
 * -----------------------------------------------------------------------------------------
 *     Version     Date            Author              Description
 * -----------------------------------------------------------------------------------------
 *     1.0         2026-06-23      Peter Dobler        Initial creation
 *     1.1         2026-06-26      Peter Dobler        Render fully from navRoutes (gate adminOnly); drop the hardcoded admin block
 *     1.2         2026-06-26      Peter Dobler        Responsive: mobile drawer + desktop collapse-to-icons; render route icons
 * -----------------------------------------------------------------------------------------
 *
 *     DEPENDENCIES
 * -----------------------------------------------------------------------------------------
 *     External        :   react react-router-dom clsx
 *     Internal        :   navRoutes nav-types useIsAdmin
 *
 *     EXPORTS
 * -----------------------------------------------------------------------------------------
 *     NavSidebar
 *
 * =========================================================================================
 */

import { NavLink } from "react-router-dom";
import { clsx } from "clsx";
import { navRoutes } from "../router/navRoutes.js";
import { useIsAdmin } from "../auth/useIsAdmin.js";

interface NavSidebarProps {
  /** Desktop icon-only collapse. */
  collapsed: boolean;
  /** Mobile off-canvas drawer open. */
  mobileOpen: boolean;
  /** Called when a nav link is clicked (closes the mobile drawer). */
  onNavigate: () => void;
}

const linkClass = (isActive: boolean, collapsed: boolean): string =>
  clsx(
    "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
    collapsed && "lg:justify-center lg:px-2",
    isActive
      ? "bg-[var(--color-bg-subtle)] text-[var(--color-accent)]"
      : "text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle)] hover:text-[var(--color-text-primary)]",
  );

/** Responsive sidebar — drawer on mobile, collapsible icon-rail on desktop. */
export function NavSidebar({ collapsed, mobileOpen, onNavigate }: NavSidebarProps): React.JSX.Element {
  const isAdmin = useIsAdmin();
  const visibleRoutes = navRoutes.filter(
    (r) => r.showInNav !== false && (r.adminOnly !== true || isAdmin),
  );

  return (
    <nav
      aria-label="Main navigation"
      className={clsx(
        "z-40 flex shrink-0 flex-col gap-1 border-r border-[var(--color-border)] bg-[var(--color-bg-card)] py-4 transition-[width,transform] duration-200",
        // Mobile/tablet: off-canvas drawer (full labels) within the content row; slides in on open.
        "absolute inset-y-0 left-0 w-60",
        mobileOpen ? "translate-x-0 shadow-lg" : "-translate-x-full",
        // Desktop: static + always visible; width depends on the collapse toggle.
        "lg:static lg:translate-x-0 lg:shadow-none",
        collapsed ? "lg:w-16" : "lg:w-56",
      )}
    >
      <ul className="flex flex-col gap-1 px-2">
        {visibleRoutes.map((route) => (
          <li key={route.path}>
            <NavLink
              to={route.path}
              onClick={onNavigate}
              title={route.label}
              className={({ isActive }) => linkClass(isActive, collapsed)}
              data-testid={`nav-link-${route.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {route.icon ? <route.icon className="h-5 w-5 shrink-0" /> : null}
              <span className={clsx("truncate", collapsed && "lg:hidden")}>{route.label}</span>
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
